import { HttpClient, httpResource } from '@angular/common/http'
import { computed, inject, Injectable } from '@angular/core'
import { rxResource } from '@angular/core/rxjs-interop'
import { shareReplay, tap } from 'rxjs'
import { ENV_TOKEN } from '../app.config'
import type { ProfilWidgetDaten } from '@saniup-webapps/shared/components'
import { initialen, mitarbeiterName } from '../components/mitarbeiter-avatar/mitarbeiter-name.util'
import { ProfilbildService } from '../components/mitarbeiter-avatar/profilbild.service'
import {
  Employee,
  EmployeeUpdate,
  MitarbeiterProfil,
  PortalNutzerAngelegt,
  PortalNutzerAnlage,
  SanitaetshausUiConfig,
} from '../model/inko-backend.model'
import { wirktAlsLeistungserbringer } from './employee-rollen.util'

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  readonly apiUrl = inject(ENV_TOKEN).api.leistungserbringerUrl
  private readonly http = inject(HttpClient)
  private readonly profilbilder = inject(ProfilbildService)

  readonly currentlyLoggedInEmployee$ = this.http
    .get<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin`)
    .pipe(shareReplay())
  readonly currentlyLoggedInEmployee = rxResource({
    stream: () => this.currentlyLoggedInEmployee$,
  })
  /**
   * Ob dieses Konto ueberhaupt eine Rolle traegt. Frisch angelegte Konten haben `roles: []` — das
   * JIT-Provisioning im Backend legt sie beim ersten Anmelden ohne Rolle an, ein Kollege muss sie
   * erst vergeben (siehe `ZugangAusstehendComponent`).
   */
  readonly hasAnyRole = computed(() => {
    if (this.currentlyLoggedInEmployee.hasValue()) {
      return this.currentlyLoggedInEmployee.value().roles.length > 0
    }
    return false
  })

  /**
   * Beide Abfragen sind im Backend auf `LEISTUNGSERBRINGER` bzw. `EXTERNAL_PARTNER` gesperrt. Ohne
   * dieses Gate feuern sie trotzdem — ein frisch angelegtes, rollenloses Konto laeuft also bei
   * JEDER Erstanmeldung in zwei 403er, die im Application-Insights wie ein Vorfall aussehen und den
   * Monitoring-Brief seit dem 06.07.2026 beschaeftigt haben. Sie sind keiner: der Nutzer bekommt
   * stattdessen den Freischaltungs-Bildschirm. `undefined` als URL laesst die Resource untaetig.
   */
  readonly employeeList = httpResource<Employee[]>(() => (this.hasAnyRole() ? `${this.apiUrl}/leistungserbringer/employees` : undefined))
  readonly uiConfig = httpResource<SanitaetshausUiConfig>(() =>
    this.hasAnyRole() ? `${this.apiUrl}/leistungserbringer/uiConfig` : undefined,
  )

  /**
   * Ob in diesem Haus ueberhaupt etwas abgeholt werden kann — steuert die Navigation.
   *
   * Fragt seit 08/2026 die FREIGABELISTEN, nicht mehr den einen Partner: ein Haus kann Inkontinenz
   * ueber Hartmann liefern und daneben einzelne Versorgungen abholen lassen. Am alten Vergleich
   * gegen den Standard-Partner haette der Reiter fuer solche Haeuser gefehlt.
   */
  readonly hasFilialabholung = computed(() => {
    const cfg = this.uiConfig.hasValue() ? this.uiConfig.value() : undefined
    // Pflegehilfsmittel kennen keinen Logistiker mehr, sondern nur noch den Abgabeweg -- HiMi
    // dagegen weiterhin eine Freigabeliste. Deshalb zwei Quellen fuer dieselbe Frage.
    return (cfg?.himiLogisticPartners ?? []).includes('FILLIABABHOLUNG') || (cfg?.phmAbgabewege ?? []).includes('FILIALABHOLUNG')
  })

  /** Die fuer HiMi freigegebenen Logistiker — Standard zuerst. Quelle des Pickers im Assistenten. */
  readonly himiLogisticPartners = computed(() => (this.uiConfig.hasValue() ? (this.uiConfig.value().himiLogisticPartners ?? []) : []))

  /**
   * Der STANDARD-Logistiker des Hauses fuer HiMi — der Rueckfall, den das Backend zieht, wenn ein
   * Vorgang selbst keinen Partner traegt (`VersorgungLogisticPartnerResolver`).
   *
   * **Das ist nicht der Partner EINES Vorgangs.** Wer wissen will, wer eine konkrete Versorgung oder
   * Bemusterung liefert, nimmt `versorgungsLogistiker(...)` aus `logistik-auswahl.model.ts`: fuehrt
   * das Haus mehrere Logistiker, weicht der Vorgang vom Standard ab. Genau diese Verwechslung liess
   * den Muster-Schritt Bemusterbarkeits-Hinweise zeigen, wo aus dem eigenen Regal gepackt wird.
   *
   * Liest ueber `hasValue()` statt `value()?.` — im Fehlerzustand wirft `value()`.
   */
  readonly himiLogisticPartnerStandard = computed(() =>
    this.uiConfig.hasValue() ? (this.uiConfig.value().himiLogisticPartner ?? null) : null,
  )

  /**
   * Die fuer Pflegehilfsmittel angebotenen Abgabewege — Versand zuerst, wenn das Haus beide fuehrt.
   * Genau ein Eintrag heisst: in der Anlage gibt es nichts zu waehlen.
   */
  readonly phmAbgabewege = computed(() => (this.uiConfig.hasValue() ? (this.uiConfig.value().phmAbgabewege ?? []) : []))

  /**
   * Ob dieses Haus mindestens eine Produktgruppe selbst fertigt (Einlagen, Maßschuhe) und damit den
   * Fertigungsmonitor braucht.
   *
   * Kommt bewusst aus der `uiConfig` und nicht daraus, ob gerade Aufträge vorliegen: der Reiter soll
   * auch dann stehen, wenn nichts darin ist — sonst wäre er genau dann weg, wenn man nachsehen will,
   * ob noch etwas offen ist.
   */
  readonly hasFertigung = computed(() => this.uiConfig.hasValue() && !!this.uiConfig.value().fertigungAvailable)

  /** Ob das Sanitaetshaus SEPA-Lastschrift anbieten kann (Stripe-Connect konfiguriert). */
  readonly sepaLastschriftAvailable = computed(() => this.uiConfig.hasValue() && this.uiConfig.value().sepaLastschriftAvailable)

  /**
   * Ob eine Rechnung am Kartenleser vor Ort kassiert werden kann — Stripe-Connect **und**
   * Terminal-Location sind hinterlegt. Ohne die Location würde das Backend mit
   * `TERMINAL_NOT_CONFIGURED` ablehnen, der Knopf gehört dann gar nicht erst in die Oberfläche.
   */
  readonly kartenzahlungTerminalAvailable = computed(() => this.uiConfig.hasValue() && this.uiConfig.value().kartenzahlungTerminalAvailable)

  /**
   * Ob dieses Haus im PG15-Anamnese-Schritt die Produktempfehlung aus Hartmanns Empfehlungsmatrix
   * angeboten bekommt (`docs/claude/inko-produktempfehlung.md` im Backend-Repo).
   *
   * Zwei Bedingungen, beide vom Backend geprueft: SaniUp hat das Haus freigeschaltet UND Paul
   * Hartmann ist als Logistiker freigegeben. **Ob zentral eine Matrix hinterlegt ist, steht hier
   * bewusst NICHT drin** — das ist ein zentraler Zustand und keine Eigenschaft des Hauses; fehlt
   * sie, sagt es der Empfehlungs-Endpunkt beim Klick.
   */
  readonly inkoProduktempfehlungAvailable = computed(() => this.uiConfig.hasValue() && this.uiConfig.value().inkoProduktempfehlungAvailable)

  /**
   * Ob dieses Haus die Leistungserbringer-Option **Hartmann CCaaS** gebucht hat — es **kann** die
   * Beratung dann je Vorgang an Hartmann CCaaS abgeben, muss aber nicht. Die Option
   * schaltet einen zweiten Knopf frei, sie stellt nichts um.
   *
   * Daran hängen zwei Dinge, und beide erscheinen sonst gar nicht: der Knopf „An Hartmann CCaaS
   * übergeben" in der Kopfleiste des HiMi-Assistenten und der Reiter „Hartmann CCaaS" auf der
   * Hilfsmittel-Seite. Anders als {@link inkoProduktempfehlungAvailable} ist das eine reine
   * Buchung und hängt insbesondere **nicht** daran, ob Paul Hartmann als Logistiker freigegeben ist.
   */
  readonly hartmannCcaasAvailable = computed(() => this.uiConfig.hasValue() && this.uiConfig.value().hartmannCcaasAvailable)

  /** Schliesst den Callcenter-Agenten ein, siehe {@link wirktAlsLeistungserbringer}. */
  readonly hasRoleLeistungserbringer = computed(() => {
    if (this.currentlyLoggedInEmployee.hasValue()) {
      return wirktAlsLeistungserbringer(this.currentlyLoggedInEmployee.value().roles)
    }
    return false
  })
  readonly hasRoleExternalPartner = computed(() => {
    if (this.currentlyLoggedInEmployee.hasValue()) {
      return this.currentlyLoggedInEmployee.value().roles.includes('EXTERNAL_PARTNER')
    }
    return false
  })
  readonly hasRoleCallcenterAgent = computed(() => {
    if (this.currentlyLoggedInEmployee.hasValue()) {
      return this.currentlyLoggedInEmployee.value().roles.includes('CALLCENTER_AGENT')
    }
    return false
  })
  /**
   * Datei-Exporte mit Daten des ganzen Hauses (Excel-Listen, Abrechnungsdateien, DATEV, Preisliste,
   * Reporting). Callcenter-Agenten sind ein externer Dienstleister und bekommen sie nicht
   * (Entscheidung Tim 2026-10-25); das Backend sperrt die Endpunkte fuer sie, hier verschwinden nur
   * die Knoepfe, die sonst in einen 403 fuehrten.
   */
  readonly darfDateiExporte = computed(
    () => this.currentlyLoggedInEmployee.hasValue() && !this.currentlyLoggedInEmployee.value().roles.includes('CALLCENTER_AGENT'),
  )

  /**
   * Logo des eigenen Hauses fuer die Kopfleiste: ein Callcenter-Agent telefoniert im Namen des
   * Hauses und sieht deshalb dessen Logo statt des SaniUp-Schriftzugs (Wunsch 2026-09-23). Nur fuer
   * ihn geladen, alle anderen behalten SaniUp.
   *
   * Als Blob, weil der Endpunkt den Token braucht. Ein Haus ohne Logo antwortet mit leerem Body;
   * dann bleibt es beim SaniUp-Logo.
   */
  readonly hausLogo = httpResource.blob(() => (this.hasRoleCallcenterAgent() ? `${this.apiUrl}/staticdata/logo` : undefined))

  /**
   * Die Antwort traegt den frischen Datensatz -- er wandert direkt in die geladene Liste, statt
   * dass der Aufrufer sie neu holt.
   *
   * Bis 2026-08-26 rief die Benutzerverwaltung hier `employeeList.reload()`. Das kostete nicht nur
   * einen zweiten GET: `lib-resource-loading` projiziert seinen Inhalt im `@else`-Zweig und blendet
   * ihn bei `status() === 'reloading'` aus -- die ganze Tabelle verschwand bei JEDEM Speichern kurz
   * aus dem DOM, samt Scrollposition und geoeffnetem Auswahlfeld.
   *
   * Ein Mitarbeiter-Cache waere hier trotzdem falsch: bis 2026-08-18 stand hier eine
   * `Map<string, Employee>`, die `getEmployeeById$` fuellte und die NIEMAND je invalidierte. Sie
   * hat nur deshalb nie einen veralteten Namen ausgeliefert, weil `getEmployeeById$` keinen
   * einzigen Aufrufer hatte. Die Resource ist die eine Quelle, sie wird fortgeschrieben.
   */
  updateEmployee$(req: EmployeeUpdate) {
    return this.http
      .put<Employee>(`${this.apiUrl}/leistungserbringer/employees`, req)
      .pipe(tap((gespeichert) => this.uebernehmeEmployee(gespeichert)))
  }

  /**
   * Legt einen Portal-Zugang fuer eine Kollegin/einen Kollegen an — Auth0-Konto und
   * Mitarbeiter-Datensatz in einem Schritt.
   *
   * **Das Sanitaetshaus steht bewusst NICHT im Request:** das Backend nimmt es aus dem Token. Wer
   * hier eine `sanitaetshausId` mitschickte, koennte sie sich nicht aussuchen — sie wuerde
   * ignoriert.
   *
   * Anders als `updateEmployee$` wird die Liste danach **neu geladen** und nicht fortgeschrieben:
   * die Antwort ist ein `PortalNutzerAngelegt` und kein vollstaendiger `Employee` (kein
   * `createdDate`, keine `externalPartnerIds`) — eine daraus zusammengesetzte Zeile waere geraten.
   * Das kostet hier nichts: Anlegen ist selten, Rollen aendern haeufig.
   */
  createPortalNutzer$(req: PortalNutzerAnlage) {
    return this.http
      .post<PortalNutzerAngelegt>(`${this.apiUrl}/leistungserbringer/portal-nutzer`, req)
      .pipe(tap(() => this.employeeList.reload()))
  }

  /**
   * Was die Kopfleiste ueber die angemeldete Person zeigt (Profil-Knopf in `libs/shared`). Das Bild
   * kommt aus dem eigenen Profil, nicht mehr aus Auth0.
   */
  readonly profilWidget = computed<ProfilWidgetDaten | null>(() => {
    if (!this.currentlyLoggedInEmployee.hasValue()) return null
    const ich = this.currentlyLoggedInEmployee.value()
    if (!ich) return null
    const name = mitarbeiterName(ich)
    return {
      name,
      email: ich.email,
      initialen: initialen(name),
      bildUrl: ich.profilbildId ? this.profilbilder.url(ich.profilbildId)() : null,
    }
  })

  /** „Ihr Profil“ speichern. Schliesst zugleich den Willkommensdialog ab (Backend). */
  profilSpeichern$(profil: MitarbeiterProfil) {
    return this.http
      .put<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin/profil`, profil)
      .pipe(tap((gespeichert) => this.uebernehmeIch(gespeichert)))
  }

  /**
   * Der im Browser zugeschnittene Ausschnitt. Das Blob wird unter der neuen Id gleich abgelegt, so
   * zeigen Kopfleiste und Vorschau das Bild ohne zweiten Abruf.
   */
  profilbildHochladen$(bild: Blob) {
    const daten = new FormData()
    daten.append('bild', bild, 'profilbild.jpg')
    return this.http.post<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin/profilbild`, daten).pipe(
      tap((gespeichert) => {
        if (gespeichert.profilbildId) this.profilbilder.vorlegen(gespeichert.profilbildId, bild)
        this.uebernehmeIch(gespeichert)
      }),
    )
  }

  profilbildEntfernen$() {
    return this.http
      .delete<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin/profilbild`)
      .pipe(tap((gespeichert) => this.uebernehmeIch(gespeichert)))
  }

  /** Schalter „Morgen-Mail“ auf „Dein Profil“: wirkt sofort, eigener Aufruf neben dem Profil. */
  aufgabenMorgenmail$(an: boolean) {
    return this.http
      .put<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin/aufgaben-morgenmail`, { an })
      .pipe(tap((gespeichert) => this.uebernehmeIch(gespeichert)))
  }

  /**
   * „E-Mail für Benachrichtigungen“ auf „Dein Profil“: gilt sofort, ohne Bestätigungslink. Leer heißt
   * Anmeldeadresse; das Backend antwortet mit 400 auf eine sichtbar kaputte Adresse.
   */
  benachrichtigungsEmail$(email: string | undefined) {
    return this.http
      .put<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin/benachrichtigungs-email`, { email })
      .pipe(tap((gespeichert) => this.uebernehmeIch(gespeichert)))
  }

  /** „Später“ im Willkommensdialog. */
  willkommenErledigt$() {
    return this.http
      .post<Employee>(`${this.apiUrl}/leistungserbringer/employees/currentlyloggedin/willkommen-erledigt`, null)
      .pipe(tap((gespeichert) => this.uebernehmeIch(gespeichert)))
  }

  /**
   * Die eigene Antwort in BEIDE Quellen: den angemeldeten Mitarbeiter (Kopfleiste, Profil) und die
   * Liste (Aufgaben, Verlauf). Ohne die Liste stuende der neue Name erst nach dem naechsten
   * Seitenaufruf an den eigenen Aufgaben.
   */
  private uebernehmeIch(gespeichert: Employee) {
    this.currentlyLoggedInEmployee.set(gespeichert)
    this.uebernehmeEmployee(gespeichert)
  }

  /**
   * Ersetzt einen Datensatz IN der geladenen Liste. `set()` setzt den Status auf `'local'` -- das
   * ist weder `'loading'` noch `'reloading'`, der Ladebalken erscheint also gar nicht erst.
   *
   * `hasValue()` ist Pflicht, nicht Stil: Angulars `BaseWritableResource.update` ist
   * `this.set(updateFn(untracked(this.value)))`, und `value` wirft im Fehlerzustand. Der Wurf
   * passiert damit im `update`-Aufruf selbst, nicht erst beim Lesen.
   */
  private uebernehmeEmployee(gespeichert: Employee) {
    if (!this.employeeList.hasValue()) {
      return
    }
    this.employeeList.update((liste) => (liste ?? []).map((e) => (e.id === gespeichert.id ? gespeichert : e)))
  }

  readonly isSaniupEmployee = computed(() => {
    const employee = this.currentlyLoggedInEmployee.hasValue() ? this.currentlyLoggedInEmployee.value() : null
    return employee?.email?.endsWith('@saniup.de') ?? false
  })
}
